import { useMedia } from 'react-use'
import { Box, BoxProps, Container, Grid, GridProps, Stack, Typography } from '@mui/material'
import { ImageOffIcon } from 'lucide-react'

import { Button } from '@/components/base'
import BaseMedia from '@/components/base/media'
import { isHappa } from '@/hooks'

import { WPLink, WPMedia } from '@/types'

export type MediaBlock = {
  media: WPMedia
  media_mobile?: WPMedia
  info: {
    align: 'top' | 'center' | 'bottom'
    title: string
    text: string
    button_1: {
      link: WPLink
      icon: string
    }
    button_2: {
      link: WPLink
      icon: string
    }
    is_first?: boolean
  }
}

/**
 * Media
 */
export type MediaProps = GridProps & Partial<MediaBlock> & { invertButtons?: boolean }

export const Media = ({
  info,
  media: media_desktop,
  media_mobile,
  invertButtons,
  children,
  sx,
  ...props
}: MediaProps) => {
  const isSM = useMedia('(max-width: 767px)', true)
  const media = isSM ? media_mobile || media_desktop : media_desktop
  const hasMedia = media && (media?.image?.url || media?.video?.url)
  const hasContent =
    info && (info?.title || info?.text || info?.button_1?.link?.url || info?.button_2?.link?.url)

  return (
    <Grid
      container
      sx={{
        display: 'grid',
        width: '100%',
        gap: { xs: 4, sm: hasMedia ? 0 : 8 },
        position: 'relative',
        ...sx
      }}
      {...props}
    >
      <Box
        sx={{
          display: 'flex',
          overflow: 'hidden',
          gridRow: { sm: '1 / -1' },
          gridColumn: { sm: '1 / -1' },
          ...(isHappa && {
            '@media (min-width: 768px) and (max-width: 1399px)': {
              position: 'absolute',
              inset: 0
            }
          })
        }}
      >
        {hasMedia ? (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              borderRadius: (theme) => theme.shape.borderRadius6,
              aspectRatio: '16/9',
              ['&:after']: {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: isHappa
                  ? 'none'
                  : hasContent
                    ? info?.align === 'top'
                      ? 'linear-gradient(180deg,rgba(0, 0,0, 0.5) 0%, rgba(0, 0, 0, 0) 100%)'
                      : info?.align === 'center'
                        ? 'linear-gradient(0deg, rgba(0, 0, 0, 0) 0%,rgba(0, 0,0, 0.5) 50%, rgba(0, 0, 0, 0) 100%)'
                        : 'linear-gradient(0deg,rgba(0, 0,0, 0.5) 0%, rgba(0, 0, 0, 0) 100%)'
                    : 'none'
              }
            }}
          >
            <BaseMedia {...media} priority={info?.is_first} />
          </Box>
        ) : (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              borderRadius: (theme) => theme.shape.borderRadius6,
              aspectRatio: '16/9',
              backgroundColor: (theme) => theme.palette.grey[200],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ImageOffIcon size={64} style={{ color: 'rgba(0, 0, 0, 0.3)' }} />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          gridRow: { sm: '1 / -1' },
          gridColumn: { sm: '1 / -1' },
          padding: (theme) => ({ sm: `${hasMedia ? theme.spacing(11) : 0} ${theme.spacing(2)}` }),
          color: (theme) => ({
            sm: hasMedia
              ? theme.palette.text.primaryContrastText
              : theme.palette.text.secondaryContrastText
          }),
          alignItems:
            info?.align === 'top' ? 'flex-start' : info?.align === 'center' ? 'center' : 'flex-end'
        }}
      >
        <Box
          sx={{
            display: 'grid',
            width: '100%',
            maxWidth: '67.2rem',
            margin: '0 auto',
            gap: isHappa ? 4 : 2
          }}
          textAlign={{ sm: 'center' }}
        >
          {info?.title ? (
            <Typography
              variant="h3"
              component={info.is_first ? 'h1' : 'h2'}
              fontWeight={isHappa ? '700' : '400'}
            >
              {info.title}
            </Typography>
          ) : null}
          {info?.text ? (
            <Typography variant="textRegular" component="p">
              {info.text}
            </Typography>
          ) : null}
          {info?.button_1?.link?.url || info?.button_2?.link?.url ? (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="center"
            >
              {info?.button_1?.link?.url ? (
                <Button
                  href={info?.button_1.link.url}
                  variant="contained"
                  color={
                    invertButtons
                      ? isHappa
                        ? 'neutral'
                        : 'primary'
                      : isSM
                        ? 'primary'
                        : 'secondary'
                  }
                  icon={info?.button_1.icon}
                >
                  {info?.button_1.link.title}
                </Button>
              ) : null}
              {info?.button_2?.link?.url ? (
                <Button
                  href={info?.button_2.link.url}
                  variant={isSM ? 'outlined' : 'contained'}
                  color={invertButtons ? (isSM ? 'primary' : 'secondary') : 'primary'}
                  icon={info?.button_2.icon}
                >
                  {info?.button_2.link.title}
                </Button>
              ) : null}
            </Stack>
          ) : null}
          {children}
        </Box>
      </Box>
    </Grid>
  )
}

/**
 * MediaWrapper
 */
export type MediaWrapperProps = BoxProps & Partial<MediaBlock>

const MediaWrapper = ({ info, media, media_mobile, ...props }: MediaWrapperProps) => {
  return (
    <Box {...props}>
      <Container>
        <Media info={info} media={media} media_mobile={media_mobile} />
      </Container>
    </Box>
  )
}

export default MediaWrapper
